// CUSTOM_WEIGHTDESIGNER_BY_CLAUDE — the ONE authenticated client for the portal edition.
// ============================================================================================
// Ryan 2026-07-30: *"When I try to save a design in the portal, it just returns 'Unauthorized'."*
//
// Access tokens live THIRTY MINUTES (`jwt.js` → `exp: now + 1800`). The Weight Designer is a
// long-dwell tool — a rep can easily spend that long on one plate before pressing Save — and both
// callers here used to read the raw token straight out of `sessionStorage` and send it as-is:
//
//     JSON.parse(sessionStorage.getItem('rep_portal_session')).accessToken
//
// so the first save after the half-hour mark 401'd, and the panel printed the worker's own word,
// "Unauthorized", at a rep who was very much signed in. Worse, the LIST call 401s identically and
// the menu then rendered "No saved designs yet." over a perfectly healthy design — which is how a
// live design came to look deleted.
//
// The portal has always shipped the cure and the WD never called it: `RepPortal.Auth`
// (`assets/custom-rep-portal.js`) refreshes within 2 min of expiry, dedupes concurrent refreshes,
// and — crucially — `getSession()` prefers whichever of localStorage/sessionStorage holds the
// FRESHER pair. That last part matters because `/rep-refresh` ROTATES the refresh token and deletes
// the old one, so as soon as any other portal tab refreshes, this tab's frozen snapshot is not
// merely expired, it is dead. Reading through `RepPortal` picks up the live pair the other tab won.
//
// ⚠️ There were TWO copies of the old helper — `ui/designs-menu.js` and `ui/showcase-admin.js` —
// which is the project's own "when two places need the same answer, one of them will be wrong"
// rule playing out. One module now, imported by both.

export const API = 'https://rfq-worker.ryan-29b.workers.dev';

/** What a rep should read when the session cannot be rescued. Never the raw 401 body. */
const SESSION_EXPIRED = 'Your session expired — reload the page to sign in again.';

/** The portal's auth manager, when the page loaded it. Absent on the PUBLIC page under `?rep=1`
 *  (a dev/demo path that never had a session to begin with), so every use is optional-chained. */
function auth() {
  return (typeof window !== 'undefined' && window.RepPortal && window.RepPortal.Auth) || null;
}

/** The session object — for `.admin` and friends, NOT for the token (see `token()`).
 *  Falls back to the raw read so the storefront `?rep=1` path behaves exactly as it used to. */
export function portalSession() {
  const A = auth();
  if (A && A.getSession) {
    try { return A.getSession(); } catch { /* fall through to the raw read */ }
  }
  try { return JSON.parse(sessionStorage.getItem('rep_portal_session') || 'null'); }
  catch { return null; }
}

/** CUSTOM_PORTALACCESS_BY_CLAUDE (Ryan 2026-10-30) — an EXTERNAL portal user (role `agency`: a
 *  freelancer or an agency, never an admin). They get the designer, their own saved designs and the
 *  picture downloads; the quote handoff, Projects, the PDF sheets and adopting a customer's share
 *  stay team-only. ⚠ Presentation, like `isRepMode()`: rfq-worker refuses those routes to their
 *  token regardless (lib/external-access.js), this only keeps the buttons from offering them. */
export function isExternalPortalUser() {
  const s = portalSession();
  return !!(s && s.role === 'agency' && !s.admin);
}

/** A token that is actually valid — refreshed if it is within 2 minutes of expiry, and picked from
 *  whichever store another tab last wrote. Read fresh on EVERY request; never cached in a module.
 *
 *  Exported as `portalToken` below for the ONE case that cannot go through `portalApi`: a
 *  multipart upload, which must not carry a JSON Content-Type. Everything else should call
 *  `portalApi`/`portalUpload` rather than hold a token of its own — holding one is precisely the
 *  bug this module exists to have deleted. */
async function token() {
  const A = auth();
  if (A && A.getAccessToken) {
    try { return await A.getAccessToken(); } catch { /* fall through */ }
  }
  const s = portalSession();
  return (s && s.accessToken) || null;
}

/**
 * Authenticated JSON call against the worker.
 *
 * Returns the parsed body on success, or `{ ok: false, error }` — the same shape both callers
 * already branch on, so `save`/`load`/`remove` did not have to change.
 *
 * On a 401 it makes exactly one recovery attempt, in the order that costs least:
 *   1. re-ask for a token — another portal tab may have refreshed since we started, and
 *      `getSession()` hands us its pair for free;
 *   2. failing that, force a refresh of our own.
 * A second 401 — or a refresh that itself fails — is a session that cannot be saved from here.
 * That covers today's cause (a rotated/expired pair) and tomorrow's: `c57d99f` checks the session
 * epoch on `/rep-refresh` but NOT on the request path, so a password change or a revocation lands
 * as a refresh failure exactly like this, and must read as "sign in again", never "Unauthorized".
 */
export async function portalApi(path, { method = 'GET', body, signal } = {}) {
  return run((bearer) => send(path, method, body, bearer, signal), (r) => `Failed (${r.status})`);
}

/**
 * The same contract for a MULTIPART upload — `/wd-plate-upload` sends a PNG, so it cannot go
 * through `portalApi` (a FormData body must not carry an explicit Content-Type; the browser has
 * to set the boundary itself).
 *
 * ⚠ THIS IS THE CALL MOST LIKELY TO NEED THE REFRESH, not the least. A rep primes a set of plates
 * across a long design session and then sends them in one batch, so the upload loop is routinely
 * the first request to cross the thirty-minute mark — exactly the failure this module was written
 * for, arriving at the end of a slow job rather than the start of a quick one.
 */
export async function portalUpload(path, formData, { signal } = {}) {
  return run((bearer) => sendForm(path, formData, bearer, signal), (r) => `Upload failed (${r.status})`);
}

/** Token → attempt → one 401 recovery → attempt again. Shared by both entry points so the
 *  recovery policy is stated once. */
async function run(attempt, failLabel) {
  const first = await token();
  if (!first) return { ok: false, error: 'Not signed in' };

  let res = await attempt(first);
  if (res.aborted) return { ok: false, error: 'Cancelled' };
  if (res.networkError) return { ok: false, error: 'Couldn’t reach the server' };

  if (res.status === 401) {
    const retryToken = await recoverToken(first);
    if (!retryToken) return { ok: false, error: SESSION_EXPIRED };
    res = await attempt(retryToken);
    if (res.aborted) return { ok: false, error: 'Cancelled' };
    if (res.networkError) return { ok: false, error: 'Couldn’t reach the server' };
    if (res.status === 401) return { ok: false, error: SESSION_EXPIRED };
  }

  const { response, data } = res;
  if (!response.ok || !data || data.ok === false || data.success === false) {
    return { ok: false, error: (data && (data.error || data.message)) || failLabel(response) };
  }
  /* ⚠ TWO GENERATIONS OF WORKER ANSWER DIFFERENTLY. The WD's own routes reply `{ ok: true }`;
     the older project routes (`/project-list`, `/project-update`, …) reply `{ success: true }`
     and carry no `ok` at all. Normalising here means a caller never has to remember which
     vintage it is talking to — and, more to the point, never has to write its own client to
     paper over it, which is how the raw-token bug got two copies in the first place. */
  return data.ok === true ? data : { ...data, ok: true };
}

/** A token DIFFERENT from the one that just 401'd, or null if there is no rescuing this session. */
async function recoverToken(used) {
  const fresh = await token();
  if (fresh && fresh !== used) return fresh;
  const A = auth();
  if (!A || !A.refreshTokens) return null;
  try {
    const minted = await A.refreshTokens();
    return minted && minted !== used ? minted : null;
  } catch {
    return null; // rotated away, deprovisioned, or epoch superseded — all mean "log in again"
  }
}

/** The valid-right-now bearer token. See `token()` — for multipart callers only. */
export { token as portalToken };

async function send(path, method, body, bearer, signal) {
  let response;
  try {
    response = await fetch(API + path, {
      method,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${bearer}` },
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') return { aborted: true };
    return { networkError: true };
  }
  let data = null;
  try { data = await response.json(); } catch { /* non-JSON */ }
  return { response, data, status: response.status };
}

/** NO Content-Type header — the browser must set it, boundary and all. */
async function sendForm(path, formData, bearer, signal) {
  let response;
  try {
    response = await fetch(API + path, {
      method: 'POST',
      headers: { Authorization: `Bearer ${bearer}` },
      body: formData,
      signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') return { aborted: true };
    return { networkError: true };
  }
  let data = null;
  try { data = await response.json(); } catch { /* non-JSON */ }
  return { response, data, status: response.status };
}
